import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { isInCall, onCallStateChange } from "@/lib/activeCall";

interface ProtectedRouteProps {
    children: ReactNode;
}

/**
 * How often to re-check that the stored session is still usable.
 *
 * `automaticSilentRenew` only wakes up near token expiry, so a session ended server-side (admin
 * block, password change, revoked grant) can otherwise sit unnoticed on an idle tab until the next
 * API call fails with a 401.
 */
const SESSION_CHECK_INTERVAL_MS = 60_000;

/**
 * Loop guard for automatic re-authentication, in sessionStorage rather than a ref because the
 * recovery itself is a full-page navigation — a ref would reset on every attempt and never catch a
 * loop. If sign-in fails again within this window we stop retrying and show the error instead.
 */
const RECOVERY_MARKER = "athavita.auth.recoveryAt";
const RECOVERY_COOLDOWN_MS = 15_000;

function claimRecoveryAttempt(): boolean {
    try {
        const last = Number(window.sessionStorage.getItem(RECOVERY_MARKER) ?? 0);
        if (Number.isFinite(last) && Date.now() - last < RECOVERY_COOLDOWN_MS) return false;
        window.sessionStorage.setItem(RECOVERY_MARKER, String(Date.now()));
        return true;
    } catch {
        // Private mode / storage disabled — better to attempt the redirect than to dead-end.
        return true;
    }
}

function clearRecoveryMarker() {
    try {
        window.sessionStorage.removeItem(RECOVERY_MARKER);
    } catch {
        /* nothing to clear */
    }
}

/**
 * Gate for signed-in routes.
 *
 * It used to render "Redirecting to sign-in…" INSTEAD of its children whenever react-oidc-context
 * reported `isLoading` — and every navigator call (`signinSilent`, `signinRedirect`) sets `isLoading`
 * back to true, which would unmount a live MeetingRoom (hub + peer connections) along with everything
 * else. (The Sep 13 meeting drops were NOT this — see PROJECT_STATUS; this is hardening.)
 *
 * Now: once the children have mounted for a signed-in user they stay mounted through any auth
 * activity; an expired token is renewed silently in the background; and nothing ever navigates the
 * page to the sign-in screen while a call is in progress (it waits for the call to end).
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const auth = useAuth();
    const [recoveryFailed, setRecoveryFailed] = useState(false);
    const [inCall, setInCall] = useState(isInCall);
    const everMounted = useRef(false);
    if (auth.isAuthenticated && auth.user) everMounted.current = true;
    // A sign-in that had to wait for the call to end.
    const deferredSignIn = useRef(false);

    useEffect(() => onCallStateChange(() => setInCall(isInCall())), []);

    const redirectToSignIn = useCallback(() => {
        if (auth.activeNavigator) return;
        if (isInCall()) {
            deferredSignIn.current = true;
            return;
        }
        if (!claimRecoveryAttempt()) {
            setRecoveryFailed(true);
            return;
        }
        void auth.signinRedirect().catch(() => setRecoveryFailed(true));
    }, [auth]);

    // Once we're genuinely signed in, forget any earlier recovery attempt so a later expiry can
    // recover again rather than being suppressed by a stale cooldown.
    useEffect(() => {
        if (auth.isAuthenticated && auth.user && !auth.user.expired) {
            clearRecoveryMarker();
            setRecoveryFailed(false);
            deferredSignIn.current = false;
        }
    }, [auth.isAuthenticated, auth.user]);

    useEffect(() => {
        if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error) {
            redirectToSignIn();
        }
    }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, redirectToSignIn]);

    // An expired or revoked session surfaces as `auth.error` (silent renew fails once the refresh
    // token is gone). Send them back through sign-in — unless they're in a call.
    useEffect(() => {
        if (auth.error && !recoveryFailed) redirectToSignIn();
        // Intentionally not depending on `recoveryFailed`: this should run when an error appears,
        // not re-run as a consequence of giving up on it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.error, redirectToSignIn]);

    // The call ended while a sign-in was waiting for it.
    useEffect(() => {
        if (!inCall && deferredSignIn.current) {
            deferredSignIn.current = false;
            redirectToSignIn();
        }
    }, [inCall, redirectToSignIn]);

    // Periodic check: an expired token is renewed SILENTLY first (refresh token, no navigation,
    // children stay mounted). Only if that fails does the error path above send them to sign-in.
    useEffect(() => {
        if (!auth.isAuthenticated) return;
        const id = window.setInterval(() => {
            if (auth.user?.expired && !auth.activeNavigator) void auth.signinSilent();
        }, SESSION_CHECK_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [auth.isAuthenticated, auth.user, auth.activeNavigator, auth]);

    const signInAgain = (
        <button
            type="button"
            onClick={() => {
                clearRecoveryMarker();
                setRecoveryFailed(false);
                void auth.signinRedirect();
            }}
            className="mt-4 text-sm font-semibold text-primary hover:underline"
        >
            Try signing in again
        </button>
    );

    if (everMounted.current) {
        // Keep the page (and any call) up. If sign-in recovery gave up, say so without taking over.
        return (
            <>
                {children}
                {auth.error && recoveryFailed && !inCall ? (
                    <div role="alert" className="fixed inset-x-0 top-0 z-[70] flex items-center justify-center gap-3 bg-destructive px-4 py-2 text-sm text-destructive-foreground">
                        Your session has expired.
                        <button
                            type="button"
                            onClick={() => { clearRecoveryMarker(); setRecoveryFailed(false); void auth.signinRedirect(); }}
                            className="rounded bg-white/20 px-2 py-0.5 font-semibold hover:bg-white/30"
                        >
                            Sign in again
                        </button>
                    </div>
                ) : null}
            </>
        );
    }

    // Only reached when automatic recovery itself failed (e.g. the Identity Service is down) —
    // otherwise an error is transparently turned back into a sign-in.
    if (auth.error && recoveryFailed) {
        return (
            <div className="flex min-h-screen items-center justify-center p-8">
                <div className="text-center">
                    <h1 className="text-lg font-semibold text-destructive">Sign-in error</h1>
                    <p className="mt-2 text-sm text-muted-foreground">{auth.error.message}</p>
                    {signInAgain}
                </div>
            </div>
        );
    }

    if (auth.isLoading || !auth.isAuthenticated) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <p className="text-sm text-muted-foreground">Redirecting to sign-in…</p>
            </div>
        );
    }

    return <>{children}</>;
}
